"use client";

import type { ChatCitation } from "@atlas/ai";
import { NarrativeBlock, type NarrativeBlockData } from "./NarrativeBlock";

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

export interface ExecSummaryProps {
  /** High-level executive snapshot text (brief.summary). */
  summary?: string | null;
  /** Optional citations for the executive snapshot. */
  summarySources?: ChatCitation[];
  /** Problem statement narrative (brief.problemStatement). */
  problemStatement?: string | null;
  /** Optional citations for the problem statement. */
  problemStatementSources?: ChatCitation[];
  /** Why-now / current-workflow narrative (brief.whyNow). */
  whyNow?: string | null;
  /** Optional citations for the why-now section. */
  whyNowSources?: ChatCitation[];
}

/* -------------------------------------------------------------------------- */
/* Config                                                                       */
/* -------------------------------------------------------------------------- */

const BLOCKS = [
  { key: "summary", title: "Executive snapshot" },
  { key: "problemStatement", title: "Problem statement" },
  { key: "whyNow", title: "Why now" },
] as const;

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Document-style executive summary rendered as up to three NarrativeBlocks:
 * Executive snapshot, Problem statement, and Why now.
 *
 * Blocks are separated by hairline top borders (handled by NarrativeBlock's
 * `first:` Tailwind variants). Returns null when all text fields are empty.
 */
export function ExecSummary({
  summary,
  summarySources,
  problemStatement,
  problemStatementSources,
  whyNow,
  whyNowSources,
}: ExecSummaryProps) {
  const textValues: Record<string, string | null | undefined> = {
    summary,
    problemStatement,
    whyNow,
  };

  const sourcesValues: Record<string, ChatCitation[] | undefined> = {
    summary: summarySources,
    problemStatement: problemStatementSources,
    whyNow: whyNowSources,
  };

  const blocks = BLOCKS.filter((b) => textValues[b.key]);
  if (blocks.length === 0) return null;

  return (
    <div className="flex flex-col">
      {blocks.map((b) => {
        const blockData: NarrativeBlockData = {
          body: textValues[b.key] as string,
          sources: sourcesValues[b.key],
        };
        return <NarrativeBlock key={b.key} title={b.title} block={blockData} />;
      })}
    </div>
  );
}
